import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Percent, TrendingUp, Wallet, RotateCcw, XCircle, Users, Lock, CheckCircle2,
  ChevronDown, ChevronRight, Loader2, Search, Eye, Trash2, X, Calendar,
} from 'lucide-react'
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, ComposedChart, Area, Line,
} from 'recharts'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDate } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

const PALETA = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16', '#a855f7', '#0ea5e9']
const FILIAIS = [
  { id: '', nome: 'Todas as filiais' },
  { id: 1, nome: 'Filial 1 — Fiscal' },
  { id: 20, nome: 'Filial 20 — Simples' },
]

function hoje() { return new Date().toISOString().split('T')[0] }
function primeiroDiaMes() { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0] }

// ----------------------------------------------------------------------------
export default function ComissaoVendedorPage() {
  const [aba, setAba] = useState<'apuracao' | 'fechamentos' | 'produtos'>('apuracao')
  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Percent className="text-violet-600" size={24} /> Comissão de Vendedor
        </h1>
        <div className="text-xs text-ink-500 bg-violet-50 border border-violet-200 rounded px-3 py-1.5">
          Por <b>liquidez</b> — comissão sobre o recebido, deduzindo devoluções e cancelamentos
        </div>
      </div>

      <div className="flex gap-1 border-b border-ink-200">
        {([['apuracao', 'Apuração'], ['fechamentos', 'Fechamentos'], ['produtos', '% por produto']] as const).map(([k, lbl]) => (
          <button key={k} onClick={() => setAba(k)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition ${
              aba === k ? 'border-violet-600 text-violet-700' : 'border-transparent text-ink-500 hover:text-ink-700'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {aba === 'apuracao' && <Apuracao />}
      {aba === 'fechamentos' && <Fechamentos />}
      {aba === 'produtos' && <ProdutosPercent />}
    </div>
  )
}

// ----------------------------------------------------------------------------
function Apuracao() {
  const qc = useQueryClient()
  const [de, setDe] = useState(primeiroDiaMes())
  const [ate, setAte] = useState(hoje())
  const [filialId, setFilialId] = useState<string>('')
  const [vendedorId, setVendedorId] = useState<string>('')
  const [expand, setExpand] = useState<number | null>(null)

  const { data: vendedores = [] } = useQuery<any[]>({
    queryKey: ['comissao-vendedores'],
    queryFn: async () => (await api.get('/comissoes/vendedores')).data,
  })

  const params = { de, ate, filial_id: filialId || undefined, vendedor_id: vendedorId || undefined }
  const { data, isFetching } = useQuery<any>({
    queryKey: ['comissao-calcular', de, ate, filialId, vendedorId],
    queryFn: async () => (await api.get('/comissoes/calcular', { params })).data,
  })

  const fechar = useMutation({
    mutationFn: async () => (await api.post('/comissoes/fechar', {
      de, ate, filial_id: filialId ? Number(filialId) : null,
      vendedor_ids: vendedorId ? [Number(vendedorId)] : null,
    })).data,
    onSuccess: (r) => { toast.success(`${r.criados} fechamento(s) registrado(s)`); qc.invalidateQueries({ queryKey: ['comissao-fechamentos'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao fechar'),
  })

  const resumo = data?.resumo || {}
  const porVendedor: any[] = data?.por_vendedor || []
  const serie: any[] = data?.serie || []
  const donut = porVendedor.filter(v => v.valor_comissao > 0).map((v, i) => ({ nome: v.vendedor_nome, valor: v.valor_comissao, fill: PALETA[i % PALETA.length] }))

  return (
    <div className="space-y-4">
      {/* filtros */}
      <div className="bg-white border border-ink-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="label">De</label>
          <input type="date" className="input !py-1.5" value={de} onChange={e => setDe(e.target.value)} />
        </div>
        <div>
          <label className="label">Até</label>
          <input type="date" className="input !py-1.5" value={ate} onChange={e => setAte(e.target.value)} />
        </div>
        <div>
          <label className="label">Filial</label>
          <select className="input !py-1.5" value={filialId} onChange={e => setFilialId(e.target.value)}>
            {FILIAIS.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Vendedor</label>
          <select className="input !py-1.5" value={vendedorId} onChange={e => setVendedorId(e.target.value)}>
            <option value="">Todos</option>
            {vendedores.map(v => <option key={v.id} value={v.id}>{v.nome}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button onClick={() => fechar.mutate()} disabled={fechar.isPending || !porVendedor.length}
          className="btn-primary bg-violet-600 hover:bg-violet-700 flex items-center gap-1.5">
          {fechar.isPending ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />}
          Fechar período {vendedorId ? '(vendedor)' : `(${porVendedor.length})`}
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        <Kpi label="Recebido (liquidado)" valor={resumo.base_liquidada} cor="blue" icon={<Wallet size={14} />} />
        <Kpi label="Devoluções" valor={-(resumo.total_devolucoes || 0)} cor="amber" icon={<RotateCcw size={14} />} />
        <Kpi label="Cancelamentos" valor={-(resumo.total_cancelamentos || 0)} cor="rose" icon={<XCircle size={14} />} />
        <Kpi label="Base líquida" valor={resumo.base_liquida} cor="violet" icon={<TrendingUp size={14} />} />
        <Kpi label="Comissão total" valor={resumo.total_comissao} cor="emerald" icon={<Percent size={14} />} destaque />
        <Kpi label="Vendedores" valor={String(resumo.qtd_vendedores ?? 0)} cor="blue" icon={<Users size={14} />} />
      </div>

      {isFetching && <div className="text-xs text-ink-400 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> calculando…</div>}

      {/* gráficos */}
      {porVendedor.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="bg-white border border-ink-200 rounded-lg p-3 lg:col-span-2">
            <h3 className="text-xs font-semibold text-ink-700 uppercase mb-2">Comissão por vendedor</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={porVendedor.slice(0, 12)} margin={{ left: 8, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
                <XAxis dataKey="vendedor_nome" tick={{ fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: number) => formatMoney(v)} />
                <Bar dataKey="valor_comissao" name="Comissão" radius={[4, 4, 0, 0]}>
                  {porVendedor.slice(0, 12).map((_, i) => <Cell key={i} fill={PALETA[i % PALETA.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-ink-200 rounded-lg p-3">
            <h3 className="text-xs font-semibold text-ink-700 uppercase mb-2">Participação</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={donut} dataKey="valor" nameKey="nome" cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2}>
                  {donut.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Pie>
                <Tooltip formatter={(v: number) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {serie.length > 1 && (
        <div className="bg-white border border-ink-200 rounded-lg p-3">
          <h3 className="text-xs font-semibold text-ink-700 uppercase mb-2">Evolução — recebido x comissão ({data?.granularidade === 'mes' ? 'mensal' : 'diária'})</h3>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={serie} margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef0f3" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(0)}k`} />
              <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `R$${(v / 1000).toFixed(1)}k`} />
              <Tooltip formatter={(v: number) => formatMoney(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Area yAxisId="l" type="monotone" dataKey="recebido" name="Recebido" stroke="#3b82f6" fill="#dbeafe" strokeWidth={2} />
              <Line yAxisId="r" type="monotone" dataKey="comissao" name="Comissão" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* tabela */}
      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600 text-xs uppercase">
            <tr>
              <th className="text-left p-2 w-6"></th>
              <th className="text-left p-2">Vendedor</th>
              <th className="text-right p-2">Recebido</th>
              <th className="text-right p-2">Devoluções</th>
              <th className="text-right p-2">Cancelam.</th>
              <th className="text-right p-2">Base líquida</th>
              <th className="text-right p-2">% efetivo</th>
              <th className="text-right p-2">Comissão</th>
            </tr>
          </thead>
          <tbody>
            {porVendedor.map((v) => (
              <>
                <tr key={v.vendedor_id} className="border-t border-ink-100 hover:bg-ink-50/60 cursor-pointer"
                  onClick={() => setExpand(expand === v.vendedor_id ? null : v.vendedor_id)}>
                  <td className="p-2 text-ink-400">{expand === v.vendedor_id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                  <td className="p-2 font-medium">{v.vendedor_nome}</td>
                  <td className="p-2 text-right font-mono">{formatMoney(v.base_liquidada)}</td>
                  <td className="p-2 text-right font-mono text-amber-700">{v.total_devolucoes ? '−' + formatMoney(v.total_devolucoes) : '—'}</td>
                  <td className="p-2 text-right font-mono text-rose-700">{v.total_cancelamentos ? '−' + formatMoney(v.total_cancelamentos) : '—'}</td>
                  <td className="p-2 text-right font-mono">{formatMoney(v.base_liquida)}</td>
                  <td className="p-2 text-right font-mono text-ink-500">{Number(v.percentual_efetivo).toFixed(2)}%</td>
                  <td className="p-2 text-right font-mono font-bold text-emerald-700">{formatMoney(v.valor_comissao)}</td>
                </tr>
                {expand === v.vendedor_id && (
                  <tr className="bg-ink-50/40">
                    <td colSpan={8} className="p-0">
                      <DetalheTitulos titulos={v.titulos} />
                    </td>
                  </tr>
                )}
              </>
            ))}
            {!porVendedor.length && !isFetching && (
              <tr><td colSpan={8} className="p-8 text-center text-ink-400">Nenhuma comissão no período/filtro.</td></tr>
            )}
          </tbody>
          {porVendedor.length > 0 && (
            <tfoot className="bg-ink-50 font-semibold">
              <tr className="border-t-2 border-ink-200">
                <td></td><td className="p-2">Total</td>
                <td className="p-2 text-right font-mono">{formatMoney(resumo.base_liquidada)}</td>
                <td className="p-2 text-right font-mono text-amber-700">−{formatMoney(resumo.total_devolucoes)}</td>
                <td className="p-2 text-right font-mono text-rose-700">−{formatMoney(resumo.total_cancelamentos)}</td>
                <td className="p-2 text-right font-mono">{formatMoney(resumo.base_liquida)}</td>
                <td></td>
                <td className="p-2 text-right font-mono text-emerald-700">{formatMoney(resumo.total_comissao)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function DetalheTitulos({ titulos = [] }: { titulos: any[] }) {
  if (!titulos.length) return <div className="p-3 text-xs text-ink-400">Sem títulos.</div>
  const meta: any = {
    liquidacao: { lbl: 'Recebimento', cor: 'text-emerald-700' },
    devolucao: { lbl: 'Devolução', cor: 'text-amber-700' },
    cancelamento: { lbl: 'Cancelamento', cor: 'text-rose-700' },
  }
  return (
    <div className="px-3 py-2">
      <table className="w-full text-xs">
        <thead className="text-ink-500">
          <tr>
            <th className="text-left p-1">Tipo</th><th className="text-left p-1">Pedido</th>
            <th className="text-left p-1">Cliente</th><th className="text-left p-1">Data</th>
            <th className="text-right p-1">Valor</th><th className="text-right p-1">%</th><th className="text-right p-1">Comissão</th>
          </tr>
        </thead>
        <tbody>
          {titulos.map((t, i) => (
            <tr key={i} className="border-t border-ink-100">
              <td className={`p-1 ${meta[t.tipo]?.cor || ''}`}>{meta[t.tipo]?.lbl || t.tipo}</td>
              <td className="p-1 font-mono">#{t.pedido_numero}</td>
              <td className="p-1 truncate max-w-[220px]">{t.cliente_nome || '—'}</td>
              <td className="p-1">{t.data ? formatDate(t.data) : '—'}</td>
              <td className={`p-1 text-right font-mono ${t.valor < 0 ? 'text-rose-600' : ''}`}>{formatMoney(t.valor)}</td>
              <td className="p-1 text-right font-mono text-ink-500">{Number(t.percentual).toFixed(2)}%</td>
              <td className={`p-1 text-right font-mono ${t.comissao < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{formatMoney(t.comissao)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ----------------------------------------------------------------------------
function Fechamentos() {
  const qc = useQueryClient()
  const [status, setStatus] = useState('')
  const [detalhe, setDetalhe] = useState<any>(null)

  const { data: lista = [], isLoading } = useQuery<any[]>({
    queryKey: ['comissao-fechamentos', status],
    queryFn: async () => (await api.get('/comissoes/fechamentos', { params: { status: status || undefined } })).data,
  })

  const acao = useMutation({
    mutationFn: async ({ id, op }: { id: number; op: string }) => {
      if (op === 'excluir') return (await api.delete(`/comissoes/fechamentos/${id}`)).data
      return (await api.post(`/comissoes/fechamentos/${id}/${op}`)).data
    },
    onSuccess: () => { toast.success('Atualizado'); qc.invalidateQueries({ queryKey: ['comissao-fechamentos'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const verDetalhe = async (id: number) => {
    const d = (await api.get(`/comissoes/fechamentos/${id}`)).data
    setDetalhe(d)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select className="input !py-1.5 max-w-[180px]" value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">Todos os status</option>
          <option value="aberto">Em aberto</option>
          <option value="pago">Pagos</option>
        </select>
      </div>
      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600 text-xs uppercase">
            <tr>
              <th className="text-left p-2">Vendedor</th><th className="text-left p-2">Período</th>
              <th className="text-right p-2">Base líquida</th><th className="text-right p-2">% efet.</th>
              <th className="text-right p-2">Comissão</th><th className="text-center p-2">Status</th>
              <th className="text-right p-2">Ações</th>
            </tr>
          </thead>
          <tbody>
            {lista.map(f => (
              <tr key={f.id} className="border-t border-ink-100 hover:bg-ink-50/60">
                <td className="p-2 font-medium">{f.vendedor_nome}</td>
                <td className="p-2 text-ink-600">{formatDate(f.periodo_ini)} – {formatDate(f.periodo_fim)}</td>
                <td className="p-2 text-right font-mono">{formatMoney(f.base_liquida)}</td>
                <td className="p-2 text-right font-mono text-ink-500">{Number(f.percentual_efetivo).toFixed(2)}%</td>
                <td className="p-2 text-right font-mono font-bold text-emerald-700">{formatMoney(f.valor_comissao)}</td>
                <td className="p-2 text-center">
                  {f.status === 'pago'
                    ? <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800"><CheckCircle2 size={11} /> pago</span>
                    : <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800">aberto</span>}
                </td>
                <td className="p-2">
                  <div className="flex items-center justify-end gap-1">
                    <button title="Ver detalhe" onClick={() => verDetalhe(f.id)} className="p-1 hover:bg-ink-100 rounded"><Eye size={15} /></button>
                    {f.status === 'aberto'
                      ? <button title="Marcar pago" onClick={() => acao.mutate({ id: f.id, op: 'pagar' })} className="p-1 hover:bg-emerald-100 rounded text-emerald-700"><CheckCircle2 size={15} /></button>
                      : <button title="Reabrir" onClick={() => acao.mutate({ id: f.id, op: 'reabrir' })} className="p-1 hover:bg-amber-100 rounded text-amber-700"><RotateCcw size={15} /></button>}
                    <button title="Excluir" onClick={() => pedirExclusao({ descricao: 'o fechamento', onConfirm: () => acao.mutate({ id: f.id, op: 'excluir' }) })} className="p-1 hover:bg-rose-100 rounded text-rose-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!lista.length && !isLoading && <tr><td colSpan={7} className="p-8 text-center text-ink-400">Nenhum fechamento registrado.</td></tr>}
          </tbody>
        </table>
      </div>

      {detalhe && (
        <div className="fixed inset-0 bg-ink-950/60 flex items-center justify-center z-50 p-4" onClick={() => setDetalhe(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white">
              <div>
                <h3 className="font-semibold">{detalhe.vendedor_nome}</h3>
                <p className="text-xs text-ink-500 flex items-center gap-1"><Calendar size={12} /> {formatDate(detalhe.periodo_ini)} – {formatDate(detalhe.periodo_fim)} · comissão <b className="text-emerald-700">{formatMoney(detalhe.valor_comissao)}</b></p>
              </div>
              <button onClick={() => setDetalhe(null)} className="p-1 hover:bg-ink-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-3">
              <DetalheTitulos titulos={detalhe.detalhe?.titulos || []} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ----------------------------------------------------------------------------
function ProdutosPercent() {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [edits, setEdits] = useState<Record<number, string>>({})

  const { data: produtos = [], isLoading } = useQuery<any[]>({
    queryKey: ['comissao-produtos', busca],
    queryFn: async () => (await api.get('/comissoes/produtos', { params: { busca: busca || undefined, limit: 300 } })).data,
  })

  const salvar = useMutation({
    mutationFn: async ({ id, pct }: { id: number; pct: number }) =>
      (await api.patch(`/comissoes/produtos/${id}`, { comissao_percent: pct })).data,
    onSuccess: () => { toast.success('% atualizado'); qc.invalidateQueries({ queryKey: ['comissao-produtos'] }) },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const onBlur = (p: any) => {
    const raw = edits[p.id]
    if (raw === undefined) return
    const pct = parseFloat(raw)
    if (isNaN(pct) || pct === Number(p.comissao_percent)) { setEdits(e => { const n = { ...e }; delete n[p.id]; return n }); return }
    salvar.mutate({ id: p.id, pct })
    setEdits(e => { const n = { ...e }; delete n[p.id]; return n })
  }

  return (
    <div className="space-y-3">
      <div className="bg-violet-50 border border-violet-200 rounded p-3 text-xs text-ink-600">
        A comissão é calculada <b>por produto</b>: cada item do pedido usa este % e o valor recebido é rateado entre os itens. Edite e tecle Enter (ou saia do campo) pra salvar.
      </div>
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400" />
        <input className="input !py-1.5 pl-8" placeholder="Buscar por código ou descrição…" value={busca} onChange={e => setBusca(e.target.value)} />
      </div>
      <div className="bg-white border border-ink-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-50 text-ink-600 text-xs uppercase">
            <tr><th className="text-left p-2">Código</th><th className="text-left p-2">Produto</th><th className="text-right p-2 w-40">Comissão %</th></tr>
          </thead>
          <tbody>
            {produtos.map(p => (
              <tr key={p.id} className="border-t border-ink-100 hover:bg-ink-50/60">
                <td className="p-2 font-mono text-ink-500">{p.codigo}</td>
                <td className="p-2">{p.descricao}</td>
                <td className="p-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <input type="number" step="0.01" min="0" max="100"
                      className="input !py-1 w-24 text-right font-mono"
                      value={edits[p.id] ?? Number(p.comissao_percent).toFixed(2)}
                      onChange={e => setEdits(s => ({ ...s, [p.id]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                      onBlur={() => onBlur(p)} />
                    <span className="text-ink-400 text-xs">%</span>
                  </div>
                </td>
              </tr>
            ))}
            {!produtos.length && !isLoading && <tr><td colSpan={3} className="p-8 text-center text-ink-400">Nenhum produto.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------
function Kpi({ label, valor, cor = 'blue', icon, destaque }: any) {
  const cores: any = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    violet: 'bg-violet-50 border-violet-200 text-violet-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  }
  return (
    <div className={`border rounded-lg p-3 ${cores[cor] || cores.blue} ${destaque ? 'ring-2 ring-emerald-300' : ''}`}>
      <div className="text-[10px] uppercase font-semibold opacity-70 truncate flex items-center gap-1">{icon} {label}</div>
      <div className="text-lg font-bold font-mono mt-0.5">
        {typeof valor === 'number' ? formatMoney(valor) : valor}
      </div>
    </div>
  )
}
